import React, { memo, useEffect, useRef } from 'react'

import { getAttachmentAssetKey } from '../../shared/hooks/useAttachmentTypes'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import ReactPlayer from 'react-player'

import { useAppDispatch } from '@/redux/hooks'
import { setVideoPlaying } from '@/redux/ui/uiSlice'

interface IncidentNoteVideoPlayerIProps {
  height?: string | number
  play?: boolean
  videoUrl: string
  width?: string | number
}

const IncidentNoteVideoPlayerComponent = ({
  height = '100%',
  play = false,
  videoUrl,
  width = '100%',
}: IncidentNoteVideoPlayerIProps) => {
  const dispatch = useAppDispatch()
  const isDownloadEnabled = useFeatureFlag(
    FeatureFlag.downloadableIncidentVideos
  )

  // isVideoPlaying is a single drawer-wide flag with no reference counting, so
  // only the instance that set it may clear it — an idle player unmounting must
  // not resume polling while another player is still mid-playback.
  const isPlayingRef = useRef(false)

  const handlePlay = () => {
    isPlayingRef.current = true
    dispatch(setVideoPlaying(true))
  }

  const handlePause = () => {
    isPlayingRef.current = false
    dispatch(setVideoPlaying(false))
  }

  useEffect(
    () => () => {
      if (isPlayingRef.current) {
        dispatch(setVideoPlaying(false))
      }
    },
    [dispatch]
  )

  return (
    <ReactPlayer
      config={
        isDownloadEnabled
          ? undefined
          : {
              file: {
                attributes: {
                  // Disable download button in video controls
                  controlsList: 'nodownload',
                },
              },
            }
      }
      controls
      data-testid='incident-note-video-player'
      height={height}
      loop
      muted
      // Disable right-click context menu to prevent video download options when FF is false
      onContextMenu={isDownloadEnabled ? undefined : (e) => e.preventDefault()}
      onPause={handlePause}
      onPlay={handlePlay}
      playing={play}
      url={videoUrl}
      width={width}
    />
  )
}

// Polling re-signs the presigned query string every three seconds while the
// asset itself is unchanged. Compare the stable S3 path so a refreshed
// signature does not reload the player and interrupt playback.
export const IncidentNoteVideoPlayer = memo(
  IncidentNoteVideoPlayerComponent,
  (previous, next) =>
    previous.height === next.height &&
    previous.play === next.play &&
    previous.width === next.width &&
    getAttachmentAssetKey(previous.videoUrl) ===
      getAttachmentAssetKey(next.videoUrl)
)
